import type { AppDispatch } from "app/store";
import { hiddenActions } from "contexts/hidden";
import type { DispatchHidden } from "contexts/hidden/reducer";
import { highlightActions } from "contexts/highlighted";
import type { DispatchHighlighted } from "contexts/highlighted/reducer";
import { renderActions } from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { SelectionSet } from "utils/selectionSet";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

/**
 * Everything a tree-driven selection writes: the painted highlight set, the primary
 * object, and the semantic `mainObjects` list the tree rows sync from.
 */
export interface SelectionSnapshot {
    highlightedIds: Iterable<number>;
    mainObject: number | undefined;
    mainObjects: readonly number[];
}

function sameIds(a: SelectionSet, b: SelectionSet): boolean {
    if (a === b) {
        return true;
    }
    if (a.size !== b.size) {
        return false;
    }
    const aIds = a.sortedIds;
    const bIds = b.sortedIds;
    for (let i = 0; i < aIds.length; i++) {
        if (aIds[i] !== bIds[i]) {
            return false;
        }
    }
    return true;
}

export function isSelectionEmpty(snapshot: SelectionSnapshot): boolean {
    const highlighted = SelectionSet.fromIds(snapshot.highlightedIds);
    return highlighted.size === 0 && snapshot.mainObject === undefined && snapshot.mainObjects.length === 0;
}

/**
 * Record a tree selection change as one undo entry that swaps between two full
 * snapshots. Object Tree V2 replaces the selection wholesale on single/range click
 * and edits it on toggle, so snapshotting `before`/`after` covers every mode with
 * one shape rather than replaying per-mode deltas.
 *
 * Dispatch order matches the tree writers — highlight, `mainObject`, then
 * `mainObjects` last — since the window's `[mainObjects]` effect is the one that
 * finally re-derives the selected rows from Redux.
 */
export function pushSelectionUndo({
    dispatch,
    dispatchHighlighted,
    description,
    before,
    after,
}: {
    dispatch: AppDispatch;
    dispatchHighlighted: DispatchHighlighted;
    description: string;
    before: SelectionSnapshot;
    after: SelectionSnapshot;
}): void {
    // `SelectionSet.fromIds` dedupes: a range that spans a folder and one of its
    // expanded children yields the child's id twice, and the shrinker wants distinct ids.
    const beforeHighlightSet = SelectionSet.fromIds(before.highlightedIds);
    const afterHighlightSet = SelectionSet.fromIds(after.highlightedIds);
    const beforeMainObjectsSet = SelectionSet.fromIds(before.mainObjects);
    const afterMainObjectsSet = SelectionSet.fromIds(after.mainObjects);
    // A no-op entry would still clear the redo stack.
    if (
        before.mainObject === after.mainObject &&
        sameIds(beforeHighlightSet, afterHighlightSet) &&
        sameIds(beforeMainObjectsSet, afterMainObjectsSet)
    ) {
        return;
    }
    const beforeHighlight = snapshotUndoIds(beforeHighlightSet);
    const afterHighlight = snapshotUndoIds(afterHighlightSet);
    const beforeMainObjects = snapshotUndoIds(beforeMainObjectsSet);
    const afterMainObjects = snapshotUndoIds(afterMainObjectsSet);
    const { mainObject: beforeMainObject } = before;
    const { mainObject: afterMainObject } = after;

    const apply = (
        highlight: typeof beforeHighlight,
        mainObject: number | undefined,
        mainObjects: typeof beforeMainObjects,
    ) => {
        dispatchHighlighted(highlightActions.setIds(restoreUndoIds(highlight)));
        dispatch(renderActions.setMainObject(mainObject));
        dispatch(renderActions.setMainObjects(Array.from(restoreUndoIds(mainObjects).sortedIds)));
    };

    dispatch(
        undoRedoActions.push({
            description,
            undoCallback: () => apply(beforeHighlight, beforeMainObject, beforeMainObjects),
            redoCallback: () => apply(afterHighlight, afterMainObject, afterMainObjects),
        }),
    );
}

/**
 * Record a hide/show from the tree as one undo entry. Only the ids whose hidden
 * membership actually flipped are kept: hiding a folder whose child was already hidden
 * must not unhide that child on undo, and showing must not re-hide what was visible.
 * Returns the ids recorded, or an empty list when nothing changed (no entry pushed).
 * Like the V1 tree eye, this restores hidden membership only, not `lastHiddenIds`.
 */
export function pushVisibilityUndo({
    dispatch,
    dispatchHidden,
    affectedIds,
    hiddenBefore,
    shown,
}: {
    dispatch: AppDispatch;
    dispatchHidden: DispatchHidden;
    affectedIds: readonly number[];
    hiddenBefore: SelectionSet;
    shown: boolean;
}): number[] {
    const changedIds = shown
        ? affectedIds.filter((id) => hiddenBefore.has(id))
        : affectedIds.filter((id) => !hiddenBefore.has(id));
    if (changedIds.length === 0) {
        return changedIds;
    }

    const shrunk = snapshotUndoIds(SelectionSet.fromIds(changedIds));

    dispatch(
        undoRedoActions.push({
            description: shown ? "Show objects" : "Hide objects",
            undoCallback: () => {
                const ids = restoreUndoIds(shrunk);
                dispatchHidden(shown ? hiddenActions.add(ids) : hiddenActions.remove(ids));
            },
            redoCallback: () => {
                const ids = restoreUndoIds(shrunk);
                dispatchHidden(shown ? hiddenActions.remove(ids) : hiddenActions.add(ids));
            },
        }),
    );
    return changedIds;
}
